import { Link } from '@inertiajs/react'
import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, Webhook } from 'lucide-react'

interface WebhookRow {
  id: string
  url: string
  description: string | null
  subscribed_events: string[]
  active: boolean
  application_name: string
  created_at: string
}

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  webhooks: WebhookRow[]
  applications: { id: string; name: string }[]
}

export default function Index({ user, organization, webhooks }: Props) {
  return (
    <AppShell user={user} organization={organization}>
      <div className="space-y-8">
        <PageHeader
          eyebrow="Webhooks"
          title="Webhooks"
          description="Receive HTTP callbacks when room events occur. Payloads are HMAC-SHA256 signed."
          actions={
            <Button asChild>
              <Link href="/dashboard/webhooks/new">
                <Plus className="mr-2 h-4 w-4" />
                New Webhook
              </Link>
            </Button>
          }
        />

        {webhooks.length === 0 ? (
          <div className="surface-card flex flex-col items-center justify-center px-6 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-foreground/[0.04]">
              <Webhook className="h-6 w-6 text-foreground/70" strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight">
              No webhooks yet
            </h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Create a webhook endpoint to receive HTTP callbacks when participants join, leave, or rooms end.
            </p>
            <Button asChild className="mt-5">
              <Link href="/dashboard/webhooks/new">Create Webhook</Link>
            </Button>
          </div>
        ) : (
          <div className="surface-card divide-y divide-foreground/[0.06] overflow-hidden">
            {webhooks.map((webhook) => (
              <Link
                key={webhook.id}
                href={`/dashboard/webhooks/${webhook.id}`}
                className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-foreground/[0.025]"
              >
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-mono text-sm">{webhook.url}</p>
                    <Badge variant={webhook.active ? 'default' : 'secondary'}>
                      {webhook.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{webhook.application_name}</span>
                    <span aria-hidden>·</span>
                    {webhook.subscribed_events.map((evt) => (
                      <Badge key={evt} variant="outline" className="text-xs">
                        {evt}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  )
}
